import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import type { MailingOmit } from '#mailing/types';

import { Uri } from '#constants/uri';

/**
 * Upserts (creates or updates) a mailing omit entry for a specific mailing.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The unique identifier of the mailing to which the omit belongs.
 * @param data - The mailing omit data to be upserted. Must include a `mailingId`
 * property matching the provided `mailingId`.
 * @returns A promise that resolves to the operation result of the upsert action.
 * @throws {Error} If `mailingId` or `data` is not provided, or if `data.mailingId` does not
 * match the provided `mailingId`.
 */
export const upsertMailingOmit = async (instance: KyInstance, mailingId?: number, data?: MailingOmit) => {
  try {
    if (isNotDefined(mailingId) || isNotDefined(data)) {
      throw new Error('MailingId and omits are required.');
    }

    if (data.mailingId !== mailingId) {
      throw new Error('MailingId in omit must match the provided mailingId.');
    }

    return await instance
      .post<OperationResult>(formatString(Uri.mailing.upsertMailingOmits, mailingId.toString()), {
        json: data,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
